/**
 * @fileoverview SolicitudesUpgrade
 * @description Panel de administración para gestionar solicitudes de upgrade
 */

import React, { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { initFirebase } from "../../firebaseConfig";
import { signInWithGoogle, signOutUser } from "../../services/api/modules/auth";
import { getAuthHeaders } from "../../services/api/utils/fetchWrapper";
import { API_URL } from "../../services/api/config";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Mapeo de estados a colores y etiquetas
const estadoConfig = {
  pendiente: {
    color: '#FEF3C7',
    textColor: '#92400E',
    borderColor: '#FDE68A',
    label: 'Pendiente',
    icon: 'bi-clock'
  },
  contactado: {
    color: '#DBEAFE',
    textColor: '#1E40AF',
    borderColor: '#93C5FD',
    label: 'Contactado',
    icon: 'bi-telephone'
  },
  completado: {
    color: '#D1FAE5',
    textColor: '#065F46',
    borderColor: '#6EE7B7',
    label: 'Completado',
    icon: 'bi-check-circle'
  }
};

function SolicitudesUpgrade() {
  const [adminUser, setAdminUser] = useState(null);
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [actualizando, setActualizando] = useState(null);

  // Inicializar auth
  useEffect(() => {
    let unsubscribe;
    async function inicializarAuth() {
      const { auth } = await initFirebase();
      unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user) {
          await user.getIdToken(true);
          setAdminUser(user);
          cargarSolicitudes(1, '');
        } else {
          setAdminUser(null);
          setLoading(false);
        }
      });
    }
    inicializarAuth();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const cargarSolicitudes = async (pagina = 1, estado = '') => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({
        page: pagina.toString(),
        pageSize: '20'
      });
      if (estado) {
        params.append('estado', estado);
      }

      const response = await fetch(`${API_URL}/solicitudes-upgrade?${params}`, {
        headers
      });

      if (!response.ok) {
        throw new Error('Error al cargar solicitudes');
      }

      const data = await response.json();
      setSolicitudes(data.solicitudes || []);
      setTotalPages(data.totalPages || 1);
      setPage(pagina);

    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  };

  const actualizarEstado = async (id, nuevoEstado) => {
    setActualizando(id);
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${API_URL}/solicitudes-upgrade/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ estado: nuevoEstado })
      });

      if (!response.ok) {
        throw new Error('Error al actualizar estado');
      }

      toast.success(`Estado actualizado a "${nuevoEstado}"`);

      // Actualizar lista local
      setSolicitudes(prev =>
        prev.map(s =>
          s.id === id ? { ...s, estado: nuevoEstado } : s
        )
      );

    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al actualizar estado');
    } finally {
      setActualizando(null);
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Si no hay usuario logueado
  if (!adminUser) {
    return (
      <div className="container mt-5 text-center">
        <h2 className="mb-3">Acceso Restringido</h2>
        <p className="text-muted mb-4">Solo administradores pueden ver esta pagina</p>
        <button onClick={signInWithGoogle} className="btn btn-warning">
          Iniciar Sesion con Google
        </button>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column min-vh-100">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <Header />

      {/* Content */}
      <div className="flex-grow-1 container py-4">
        {/* Breadcrumb y título */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-1">
                <li className="breadcrumb-item">
                  <a href="/admin-ops" className="text-decoration-none">Admin</a>
                </li>
                <li className="breadcrumb-item active">Solicitudes de Upgrade</li>
              </ol>
            </nav>
            <h4 className="mb-0 fw-bold">
              <i className="bi bi-arrow-up-circle me-2"></i>
              Solicitudes de Upgrade
            </h4>
          </div>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => signOutUser()}
          >
            Cerrar Sesion
          </button>
        </div>

        {/* Filtros */}
        <div className="card mb-4">
          <div className="card-body py-2">
            <div className="d-flex align-items-center gap-3">
              <span className="text-muted small">Filtrar por estado:</span>
              <div className="btn-group" role="group">
                <button
                  className={`btn btn-sm ${filtroEstado === '' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => {
                    setFiltroEstado('');
                    cargarSolicitudes(1, '');
                  }}
                >
                  Todos
                </button>
                <button
                  className={`btn btn-sm ${filtroEstado === 'pendiente' ? 'btn-warning' : 'btn-outline-secondary'}`}
                  onClick={() => {
                    setFiltroEstado('pendiente');
                    cargarSolicitudes(1, 'pendiente');
                  }}
                >
                  Pendientes
                </button>
                <button
                  className={`btn btn-sm ${filtroEstado === 'contactado' ? 'btn-info' : 'btn-outline-secondary'}`}
                  onClick={() => {
                    setFiltroEstado('contactado');
                    cargarSolicitudes(1, 'contactado');
                  }}
                >
                  Contactados
                </button>
                <button
                  className={`btn btn-sm ${filtroEstado === 'completado' ? 'btn-success' : 'btn-outline-secondary'}`}
                  onClick={() => {
                    setFiltroEstado('completado');
                    cargarSolicitudes(1, 'completado');
                  }}
                >
                  Completados
                </button>
              </div>
              <button
                className="btn btn-sm btn-outline-primary ms-auto"
                onClick={() => cargarSolicitudes(page, filtroEstado)}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Actualizar
              </button>
            </div>
          </div>
        </div>

        {/* Tabla de solicitudes */}
        <div className="card">
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
              </div>
            ) : solicitudes.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-inbox" style={{ fontSize: '48px' }}></i>
                <p className="mt-3">No hay solicitudes de upgrade</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '140px' }}>Fecha</th>
                      <th>Mascota/Objeto</th>
                      <th>Usuario</th>
                      <th>Email</th>
                      <th>Telefono</th>
                      <th>Plan</th>
                      <th style={{ width: '100px' }}>Precio</th>
                      <th style={{ width: '120px' }}>Estado</th>
                      <th style={{ width: '180px' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {solicitudes.map((sol) => {
                      const config = estadoConfig[sol.estado] || estadoConfig.pendiente;
                      return (
                        <tr key={sol.id}>
                          <td className="small">{formatFecha(sol.createdAt)}</td>
                          <td>
                            <div className="fw-semibold">{sol.nombreObjeto || '-'}</div>
                            <small className="text-muted">{sol.tipoObjeto}</small>
                          </td>
                          <td>{sol.nombreObjeto || '-'}</td>
                          <td>
                            <a href={`mailto:${sol.email}`} className="text-decoration-none">
                              {sol.email || '-'}
                            </a>
                          </td>
                          <td>
                            {sol.telefono ? (
                              <a href={`tel:${sol.telefono}`} className="text-decoration-none">
                                {sol.telefono}
                              </a>
                            ) : '-'}
                          </td>
                          <td>
                            <span className="badge bg-purple text-white" style={{
                              background: sol.planSolicitado === 'premium'
                                ? 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)'
                                : 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
                            }}>
                              {sol.planSolicitado?.toUpperCase()}
                            </span>
                          </td>
                          <td className="fw-semibold">${sol.precioUpgrade} MXN</td>
                          <td>
                            <span
                              className="badge d-inline-flex align-items-center gap-1"
                              style={{
                                backgroundColor: config.color,
                                color: config.textColor,
                                border: `1px solid ${config.borderColor}`
                              }}
                            >
                              <i className={`bi ${config.icon}`}></i>
                              {config.label}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              {sol.estado !== 'contactado' && (
                                <button
                                  className="btn btn-sm btn-outline-info"
                                  onClick={() => actualizarEstado(sol.id, 'contactado')}
                                  disabled={actualizando === sol.id}
                                  title="Marcar como contactado"
                                >
                                  {actualizando === sol.id ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                  ) : (
                                    <i className="bi bi-telephone"></i>
                                  )}
                                </button>
                              )}
                              {sol.estado !== 'completado' && (
                                <button
                                  className="btn btn-sm btn-outline-success"
                                  onClick={() => actualizarEstado(sol.id, 'completado')}
                                  disabled={actualizando === sol.id}
                                  title="Marcar como completado"
                                >
                                  {actualizando === sol.id ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                  ) : (
                                    <i className="bi bi-check-lg"></i>
                                  )}
                                </button>
                              )}
                              {sol.estado !== 'pendiente' && (
                                <button
                                  className="btn btn-sm btn-outline-warning"
                                  onClick={() => actualizarEstado(sol.id, 'pendiente')}
                                  disabled={actualizando === sol.id}
                                  title="Marcar como pendiente"
                                >
                                  {actualizando === sol.id ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                  ) : (
                                    <i className="bi bi-arrow-counterclockwise"></i>
                                  )}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="card-footer d-flex justify-content-between align-items-center">
              <small className="text-muted">Pagina {page} de {totalPages}</small>
              <div className="btn-group">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page <= 1}
                  onClick={() => cargarSolicitudes(page - 1, filtroEstado)}
                >
                  <i className="bi bi-chevron-left"></i> Anterior
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page >= totalPages}
                  onClick={() => cargarSolicitudes(page + 1, filtroEstado)}
                >
                  Siguiente <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default SolicitudesUpgrade;
